import React, { useState } from "react";
import {
  X,
  Camera,
  BriefcaseBusiness,
  Wrench,
  ArrowRight,
  ShieldCheck
} from "lucide-react";

import "./profession.dashboard.css";
import API from "../../../api";
import { useNavigate } from "react-router-dom";

export default function ProfessionSetupModal({
  open,
  onClose,
  profession,
  user,
  onComplete
}) {
  const navigate = useNavigate();

  const [skills, setSkills] = useState("");
  const [experienceYears, setExperienceYears] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");

  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  /* =====================================================
     VERIFICATION REQUIREMENT
     Engineer + Architect only
  ===================================================== */

  const requiresVerification =
    profession === "Engineer" ||
    profession === "Architect";


  /* =====================================================
     PROFILE PHOTO
  ===================================================== */

  const uploadPhoto = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      setProfilePhoto(reader.result);
    };

    reader.readAsDataURL(file);
  };


  /* =====================================================
     SAVE PROFESSION
  ===================================================== */

  const submitProfession = async () => {

    const userId = user?._id || user?.id;

    if (!userId) {
      onClose();
      navigate("/");
      return;
    }

    setSubmitting(true);

    try {

      const res = await API.post(
        "/profession/update",
        {
          userId,

          profession,

          skills: skills
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),

          experienceYears:
            Number(experienceYears) || 0,

          profilePhoto
        }
      );


      /* =================================================
         API ERROR
      ================================================= */

      if (!res.data.success) {

        alert(
          res.data.message ||
          "Submission failed"
        );

        return;
      }


      /* =================================================
         UPDATE LOCAL USER
      ================================================= */

      localStorage.setItem(
        "civilink_user",
        JSON.stringify(res.data.user)
      );


      /* =================================================
         CLOSE MODAL
      ================================================= */

      onClose();


      /* =================================================
         ENGINEER / ARCHITECT
         → DEGREE VERIFICATION
      ================================================= */

      if (requiresVerification) {

        navigate("/professional-upload");

        return;
      }


      /* =================================================
         OTHER PROFESSIONS
      ================================================= */

      onComplete?.(profession);

    } catch (err) {

      console.error(
        "Profession update failed:",
        err
      );

      alert(
        "Unable to save profession. Please try again."
      );

    } finally {

      setSubmitting(false);
    }
  };


  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div
      className="profession-modal-backdrop"
      onClick={onClose}
    >

      <div
        className="profession-modal"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <div className="profession-modal-header">

          <div>

            <span className="profession-modal-label">
              CIVILINK PROFESSIONAL
            </span>

            <h2>
              Become a {profession}
            </h2>

          </div>

          <button
            type="button"
            className="profession-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={19} />
          </button>

        </div>


        {/* =================================================
            VERIFICATION MESSAGE
        ================================================= */}

        {requiresVerification && (

          <div className="profession-verification-info">

            <div className="profession-verification-icon">

              <ShieldCheck size={20} />

            </div>

            <div>

              <strong>
                Professional verification required
              </strong>

              <p>
                {profession}s need to submit
                their professional qualification
                certificate for Civilink review.
              </p>

            </div>

          </div>

        )}


        {/* =================================================
            PROFILE PHOTO
        ================================================= */}

        <div className="profession-form-field">

          <label>
            Profile Photo
          </label>

          <label className="profession-photo-upload">

            <input
              type="file"
              accept="image/*"
              onChange={uploadPhoto}
            />

            <div className="profession-photo-icon">

              {profilePhoto ? (

                <img
                  src={profilePhoto}
                  alt="Profile preview"
                />

              ) : (

                <Camera size={20} />

              )}

            </div>

            <div>

              <strong>
                {profilePhoto
                  ? "Photo selected"
                  : "Upload profile photo"}
              </strong>

              <span>
                Use a clear professional photo
              </span>

            </div>

          </label>

        </div>


        {/* =================================================
            SKILLS
        ================================================= */}

        <div className="profession-form-field">

          <label>
            Skills
          </label>

          <div className="profession-input-wrap">

            <Wrench size={17} />

            <input
              type="text"
              placeholder="e.g. AutoCAD, Site Planning, Estimation"
              value={skills}
              onChange={(e) =>
                setSkills(e.target.value)
              }
            />

          </div>

          <small>
            Separate multiple skills with commas.
          </small>

        </div>


        {/* =================================================
            EXPERIENCE
        ================================================= */}

        <div className="profession-form-field">

          <label>
            Experience
          </label>

          <div className="profession-input-wrap">

            <BriefcaseBusiness size={17} />

            <input
              type="number"
              min="0"
              placeholder="Years of experience"
              value={experienceYears}
              onChange={(e) =>
                setExperienceYears(e.target.value)
              }
            />

          </div>

        </div>


        {/* =================================================
            ACTIONS
        ================================================= */}

        <div className="profession-modal-actions">

          <button
            type="button"
            className="profession-cancel-btn"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="profession-continue-btn"
            onClick={submitProfession}
            disabled={submitting}
          >

            {submitting
              ? "Saving..."
              : requiresVerification
                ? "Continue to Verification"
                : "Save Profession"
            }

            {!submitting && (
              <ArrowRight size={17} />
            )}

          </button>

        </div>

      </div>

    </div>
  );
}